import {
  BarChart,
  Card,
  DataTable,
  ErrorState,
  FigureStrip,
  Grid,
  Money,
  OpenLink,
  PageHeader,
  RelativeTime,
  RuledHeading,
  Stack,
  StatCard,
  StatusWord,
  Text,
  Timeline,
} from '../../components/index.js'
import { fromCalendarDate, longDate, toCalendarDate, weekdayShort } from '../../lib/dates.js'
import { useGetDashboardQuery } from './api/overview.api.js'
import {
  ATTENTION_DESTINATION,
  CHART_RANGE,
  MONTHLY_FIGURES,
  CHART_TITLE,
  DAYS_IN_A_WEEK,
  SUBTITLE,
  THIS_MONTH_RANGE,
  THIS_MONTH_TITLE,
  WANTS_ATTENTION,
} from './core/constants.js'
import type { AttentionItem, Figure, OverviewProps, RecentBooking } from './core/types.js'

/**
 * 9.1 — the overview. *"What the platform did today, and anything wanting
 * attention."*
 *
 * Not a dashboard of everything: four figures, the week, and the panel of things
 * somebody has to do something about. The specification is explicit that opening
 * an attention row is the **only** action here, which is why nothing else on this
 * screen is pressable.
 */
export function Overview({ onOpen }: OverviewProps) {
  const { data, isLoading, isError, refetch } = useGetDashboardQuery()

  if (isError) {
    return (
      <Stack>
        <PageHeader title="Overview" subtitle={SUBTITLE} />
        <ErrorState message="Today's figures could not be loaded." onRetry={() => void refetch()} />
      </Stack>
    )
  }

  const t = data?.today

  return (
    <Stack>
      <PageHeader title="Overview" subtitle={SUBTITLE} />

      {/* Four figures, each with what it is measured against — "+6 on yesterday".
          A figure with nothing to compare against sends null rather than +0, and
          the strip draws nothing rather than a measured-looking zero.

          One line rather than four cards, and here it buys something specific:
          the panel below is the thing this screen exists for, and four cards
          were about eighty-five pixels of headline standing in front of it. */}
      <FigureStrip
        loading={isLoading}
        figures={[
          {
            label: 'Bookings today',
            value: t?.bookings.value ?? 0,
            comparison: comparison(t?.bookings),
          },
          {
            label: 'Taken today',
            value: <Money cents={t?.taken.value ?? 0} />,
            comparison: comparison(t?.taken, true),
          },
          {
            label: 'Live providers',
            value: t?.live_providers.value ?? 0,
            comparison: comparison(t?.live_providers),
          },
          {
            label: 'Refused payments',
            value: t?.refused_payments.value ?? 0,
            comparison: comparison(t?.refused_payments),
          },
        ]}
      />

      {/**
       * **The thing that needs somebody, first.**
       *
       * This was fourth — under today's figures, under the month's, under
       * the chart — which put it 588px down a 663px window, so the one
       * panel this screen exists for was below the fold on an ordinary
       * laptop and the first thing anybody saw was a bar chart. Its own
       * comment says it is *"the one thing this screen does"*.
       *
       * What is left above it is the four figures for today, which are a
       * headline rather than a thing to act on — and they are a strip rather
       * than four cards, so the headline is 56px instead of 140.
       */}
      <Card>
        <Text variant="body-strong" as="h2">
          {WANTS_ATTENTION}
        </Text>
        {/* The one thing this screen does. Every row opens somewhere, and where
            is decided in `core/constants.ts` rather than here. */}
        <Timeline
          loading={isLoading}
          /* See `ClassRecord`: the chain guards `data` and then walks into a
              field. A missing array should cost this panel, not the screen. */
          entries={data?.attention?.map((a: AttentionItem) => ({
            id: `${a.kind}-${a.subject_id}`,
            what:
              (
                /*
                 * It has to look like it opens something.
                 *
                 * This was `text-ink` with an underline on hover — the same ink
                 * as every other word on the panel, so the one screen whose
                 * whole job is *"every row opens somewhere"* gave no sign that
                 * any of it was clickable until the cursor happened to land on
                 * it. The brand note is explicit that Nectarine is the action
                 * colour, everywhere.
                 */
                <OpenLink
                  chevron
                  onClick={() =>
                    onOpen(ATTENTION_DESTINATION[a.kind], a.subject_id, a.label, a.kind)
                  }
                >
                  {a.label}
                </OpenLink>
              ),
            /* No actor at all. These are facts about the platform, not
               things somebody did, and `null` here draws "The platform". */
            // `at` is nullable on the wire — a category that filled up has no
            // moment, it is simply true now. An empty string would draw "just
            // now", which is a claim; RelativeTime says "Never" for nothing.
            when: a.at ?? '',
            detail: a.detail ?? undefined,
          }))}
          empty="Nothing needs you right now."
        />
      </Card>

      {/**
       * The month, under the day. **Asked for by the client on 2026-09-21.**
       *
       * The API had computed these six since it was written and no screen
       * drew one, so they were taken out; the client asked for them back
       * with somewhere to put them. They sit under today's four rather than
       * beside them because they answer a different question — what the
       * platform *is*, rather than what it did since midnight — and mixing
       * the two scales in one row is how somebody reads a month's bookings
       * as a day's.
       */}
      {/* A ruled heading over the figures, not a card around them: a
          `StatCard` is already a surface, and six of them inside another one
          is a box drawn round six boxes. */}
      <Stack gap={4}>
        <RuledHeading>{THIS_MONTH_TITLE}</RuledHeading>
        <Text variant="caption" className="text-ink-muted -mt-2 text-center">
          {THIS_MONTH_RANGE}
        </Text>
        <Grid columns={3}>
          {MONTHLY_FIGURES.map((f) => (
            <StatCard
              key={f.key}
              label={f.label}
              value={data?.counts[f.key] ?? 0}
              loading={isLoading}
            />
          ))}
          <StatCard
            label="Our fee this month"
            /* The platform's own fee, not the money that passed through it —
               which is what the repository's comment says it selects and what
               a figure headed "revenue" would be read as. */
            value={<Money cents={data?.counts.revenue_this_month_cents ?? 0} />}
            loading={isLoading}
          />
        </Grid>
      </Stack>

      <Card>
        <Text variant="body-strong" as="h2">
          {CHART_TITLE}
        </Text>
        <Text variant="caption" className="text-ink-muted">
          {CHART_RANGE}
        </Text>
        {/* A quiet day is a hairline rather than a gap: the chart says so itself,
            and the seven columns are what make it a week. */}
        <BarChart
          loading={isLoading}
          bars={data && week(data.week)}
          emptyText="No bookings this week yet."
        />
      </Card>

      {/**
       * One under the other on a phone.
       *
       * Two tables sharing a 390px row is about 180px each — a client's name
       * and nothing else, with the date it is about cut off. Said plainly:
       * *"dashboard page latest booking and latest enrolement wont fit side
       * by side in mobile screen should be one below another"*.
       *
       * `flex-col` below `sm` rather than a grid, because `Row` already
       * gives the gap and the two cards are a pair rather than a layout.
       */}
      <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start">
        <Card className="flex-1">
          <Text variant="body-strong" as="h2">
            Latest bookings
          </Text>
          <DataTable
            loading={isLoading}
            rows={data?.recent_bookings}
            rowKey={(r: RecentBooking) => r.id}
            emptyTitle="Nothing booked today"
            emptyIllustration="bookings"
            empty="Appointments booked since midnight appear here."
            columns={[
              { key: 'who', header: 'Client', cell: (r) => r.client_name },
              { key: 'with', header: 'With', cell: (r) => r.provider_name },
              { key: 'when', header: 'When', cell: (r) => <RelativeTime iso={r.starts_at} /> },
              {
                key: 'status',
                header: 'Status',
                cell: (r) => <StatusWord kind="booking" status={r.status as never} />,
              },
            ]}
          />
        </Card>

        <Card className="flex-1">
          <Text variant="body-strong" as="h2">
            Latest enrolments
          </Text>
          <DataTable
            loading={isLoading}
            rows={data?.recent_enrollments}
            rowKey={(r) => r.id}
            emptyTitle="Nobody enrolled today"
            emptyIllustration="classes"
            empty="People who join a class today appear here."
            columns={[
              { key: 'who', header: 'Client', cell: (r) => r.client_name },
              { key: 'class', header: 'Class', cell: (r) => r.class_title },
              { key: 'when', header: 'When', cell: (r) => <RelativeTime iso={r.enrolled_at} /> },
            ]}
          />
        </Card>
      </div>
    </Stack>
  )
}

/**
 * Seven columns, whatever came back.
 *
 * The server builds the week with `generate_series`, so a quiet day arrives as
 * a nought rather than as a missing row — and the chart trusted that without
 * saying so. Six rows would have drawn a six-day week that looked deliberate.
 * Anchored on the last day the server sent, so the two never disagree about
 * which week this is.
 */
function week(days: { day: string; bookings: number }[]) {
  const byDay = new Map(days.map((d) => [d.day, d.bookings]))
  const last = days.at(-1)?.day
  if (!last) return []
  const end = fromCalendarDate(last)
  return Array.from({ length: DAYS_IN_A_WEEK }, (_, i) => {
    const at = new Date(end)
    at.setDate(end.getDate() - (DAYS_IN_A_WEEK - 1 - i))
    const day = toCalendarDate(at)
    const bookings = byDay.get(day) ?? 0
    return {
      /* The weekday, not the first character of "2026-09-21", which is what
         this was and which labelled all seven columns "2". */
      label: weekdayShort(day),
      value: bookings,
      title: `${longDate(day)}: ${bookings} booking${bookings === 1 ? '' : 's'}`,
    }
  })
}

/**
 * "+6 on yesterday", or nothing at all.
 *
 * `compared_to` is null where there is nothing to measure against, and the
 * specification is firm about why: *"eleven bookings means two different things
 * depending on whether yesterday was four or forty"*, and a "+0" reads as a
 * measured result rather than as the absence of one.
 */
function comparison(figure: Figure | undefined, money = false): React.ReactNode {
  if (!figure || figure.compared_to === null) return undefined
  const delta = figure.value - figure.compared_to
  if (delta === 0) return `Same as ${period(figure.period)}`
  const sign = delta > 0 ? '+' : '−'
  const size = money ? <Money cents={Math.abs(delta)} signed={false} /> : Math.abs(delta)
  return (
    <>
      {sign}
      {size} on {period(figure.period)}
    </>
  )
}

const period = (p: Figure['period']) => (p === 'yesterday' ? 'yesterday' : 'last week')
